import React, { useState, useEffect } from 'react';
import SavedSearchConditionCard from './SavedSearchConditionCard';
import GlobalEmailNotificationToggle from './GlobalEmailNotificationToggle';

interface SearchFilters {
  keyword?: string;
  skill_ids?: number[];
  occupation_ids?: number[];
  industry_ids?: number[];
  characteristic_ids?: number[];
  prefecture_ids?: number[];
  workstyle_types?: string[];
  availability_types?: string[];
  price_over?: number;
  payment_site?: string;
  settlement_hours?: string;
  interview_count_max?: number;
  published_within?: number;
  is_recruiting?: boolean;
  remote_types?: string[];
  workday_types?: string[];
  job_source_ids?: number[];
  sort?: string;
  page?: number;
}

interface SavedSearchCondition {
  id: number;
  search_params: SearchFilters;
  is_active: boolean;
  created_at: string;
}

interface GuestEmailSubscription {
  id: number;
  email: string;
  is_active: boolean;
}

interface SavedSearchConditionsPageProps {
  initialConditions?: SavedSearchCondition[];
  isUserSignedIn?: boolean;
  guestEmailSubscription?: GuestEmailSubscription;
  token?: string;
}

const SavedSearchConditionsPage: React.FC<SavedSearchConditionsPageProps> = ({
  initialConditions = [],
  isUserSignedIn = false,
  guestEmailSubscription: initialGuestEmailSubscription,
  token
}) => {
  const [savedConditions, setSavedConditions] = useState<SavedSearchCondition[]>(initialConditions);
  const [guestEmailSubscription, setGuestEmailSubscription] = useState<GuestEmailSubscription | undefined>(initialGuestEmailSubscription);
  const [isLoading, setIsLoading] = useState(false);

  // コンポーネントマウント時にデータを取得
  useEffect(() => {
    fetchSavedSearchConditions();
  }, []);

  // 保存済み検索条件を取得
  const fetchSavedSearchConditions = async () => {
    setIsLoading(true);
    try {
      const endpoint = isUserSignedIn
        ? '/saved_search_conditions'
        : '/guest_email_subscriptions/saved_search_conditions';

      const response = await fetch(endpoint, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.status === 'success') {
        setSavedConditions(data.saved_search_conditions || []);
        if (!isUserSignedIn && data.guest_email_subscription) {
          setGuestEmailSubscription(data.guest_email_subscription);
        }
      }
    } catch (error) {
      console.error('Error fetching saved search conditions:', error);
      if (window.showGlobalFlash) {
        window.showGlobalFlash('検索条件の取得に失敗しました', 'error', false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 個別の検索条件のメール受信設定を切り替え
  const handleToggle = async (id: number, isActive: boolean) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (!csrfToken) {
        throw new Error('CSRF token not found');
      }

      const response = await fetch(`/saved_search_conditions/${id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // ローカルステートを更新
        setSavedConditions(prev =>
          prev.map(condition =>
            condition.id === id
              ? { ...condition, is_active: !isActive }
              : condition
          )
        );

        if (window.showGlobalFlash) {
          window.showGlobalFlash(data.message || 'メール受信設定を更新しました', 'success', false);
        }
      } else {
        throw new Error('Failed to toggle');
      }
    } catch (error) {
      console.error('Error toggling search condition:', error);
      if (window.showGlobalFlash) {
        window.showGlobalFlash('メール受信設定の更新に失敗しました', 'error', false);
      }
    }
  };

  // 検索条件を削除
  const handleDelete = async (id: number) => {
    if (!confirm('この検索条件を削除しますか？')) {
      return;
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (!csrfToken) {
        throw new Error('CSRF token not found');
      }

      const response = await fetch(`/saved_search_conditions/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // ローカルステートから削除
        setSavedConditions(prev => prev.filter(condition => condition.id !== id));

        if (window.showGlobalFlash) {
          window.showGlobalFlash(data.message || '検索条件を削除しました', 'success', false);
        }
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting search condition:', error);
      if (window.showGlobalFlash) {
        window.showGlobalFlash('検索条件の削除に失敗しました', 'error', false);
      }
    }
  };

  // 検索条件で検索ページに移動
  const handleSearch = (searchParams: SearchFilters) => {
    const params = new URLSearchParams();
    
    if (searchParams.keyword) params.append('keyword', searchParams.keyword);
    searchParams.skill_ids?.forEach(id => params.append('skill_ids[]', id.toString()));
    searchParams.occupation_ids?.forEach(id => params.append('occupation_ids[]', id.toString()));
    searchParams.industry_ids?.forEach(id => params.append('industry_ids[]', id.toString()));
    searchParams.characteristic_ids?.forEach(id => params.append('characteristic_ids[]', id.toString()));
    searchParams.prefecture_ids?.forEach(id => params.append('prefecture_ids[]', id.toString()));
    searchParams.workstyle_types?.forEach(type => params.append('workstyle_types[]', type));
    searchParams.availability_types?.forEach(type => params.append('availability_types[]', type));
    if (searchParams.price_over) params.append('price_over', searchParams.price_over.toString());
    if (searchParams.payment_site) params.append('payment_site', searchParams.payment_site);
    if (searchParams.settlement_hours) params.append('settlement_hours', searchParams.settlement_hours);
    if (searchParams.interview_count_max) params.append('interview_count_max', searchParams.interview_count_max.toString());
    if (searchParams.published_within) params.append('published_within', searchParams.published_within.toString());
    if (searchParams.is_recruiting) params.append('is_recruiting', 'true');
    searchParams.remote_types?.forEach(type => params.append('remote_types[]', type));
    searchParams.workday_types?.forEach(type => params.append('workday_types[]', type));
    searchParams.job_source_ids?.forEach(id => params.append('job_source_ids[]', id.toString()));
    if (searchParams.sort) params.append('sort', searchParams.sort);
    
    const queryString = params.toString();
    window.location.href = `/job_listings/search${queryString ? `?${queryString}` : ''}`;
  };

  // 全体のメール配信設定を保存
  const handleSaveGlobalEmailNotification = async (isActive: boolean) => {
    if (!guestEmailSubscription) return;

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (!csrfToken) {
        throw new Error('CSRF token not found');
      }

      const response = await fetch(`/guest_email_subscriptions/${guestEmailSubscription.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          guest_email_subscription: {
            is_active: isActive
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // ローカルステートを更新
        setGuestEmailSubscription(prev =>
          prev ? { ...prev, is_active: isActive } : prev
        );

        if (window.showGlobalFlash) {
          window.showGlobalFlash(data.message || 'メール配信設定を更新しました', 'success', false);
        }
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating global email notification:', error);
      if (window.showGlobalFlash) {
        window.showGlobalFlash('メール配信設定の更新に失敗しました', 'error', false);
      }
    }
  };

  return (
    <div>
      {/* 全体のメール配信設定（ゲストユーザーの場合のみ） */}
      {!isUserSignedIn && guestEmailSubscription && (
        <div className="mb-4">
          <GlobalEmailNotificationToggle
            guestEmailSubscription={guestEmailSubscription}
            onSave={handleSaveGlobalEmailNotification}
          />
        </div>
      )}

      {/* 検索条件一覧 */}
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">読み込み中...</span>
          </div>
        </div>
      ) : savedConditions.length > 0 ? (
        <div className="row g-3">
          {savedConditions.map(condition => (
            <div key={condition.id} className="col-12">
              <SavedSearchConditionCard
                condition={condition}
                onToggle={handleToggle}
                onDelete={handleDelete}
                onSearch={handleSearch}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-5">
          <i className="bi bi-search fs-1 text-muted mb-3 d-block"></i>
          <h5 className="mb-3">保存済み検索条件はありません</h5>
          <a href="/job_listings/search" className="btn btn-primary">
            新着案件を通知を受け取る
          </a>
        </div>
      )}
    </div>
  );
};

export default SavedSearchConditionsPage;

